/**
 * Phase 13: display formatters. Currency uses the tenant base currency code
 * that already lives in `LedgerEntry.currency`. Locale defaults to the
 * browser; callers can override per-call.
 */

export function fmtCurrency(value: string | number, currency = 'PHP', locale?: string): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return '—';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return num.toFixed(2);
  }
}

export function fmtNumber(value: string | number, locale?: string): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(num);
}

export function fmtDate(iso: string | Date | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtDateTime(iso: string | Date | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Friendly slot identity. Slots have no nickname column, so the label is
 * derived: an ordinal (when the caller knows the slot's position among the
 * member's active slots) plus the full code for disambiguation. Single-slot
 * members get "My Slot" since an ordinal carries no information there.
 */
export function formatSlotLabel(slotCode: string, ordinal?: number): string {
  return ordinal != null ? `Slot ${ordinal} · ${slotCode}` : `My Slot · ${slotCode}`;
}

export function initialsFromName(name: string | null | undefined, fallback = 'ME'): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
